/**
 * MMO HUD Scene
 * UI overlay that runs in parallel with MMOGameScene
 * Uses GameUITheme for consistent styling matching pre-game screens
 */

import Phaser from 'phaser';
import { ServiceLocator, Services } from '../../utils/ServiceLocator';
import { MMOStateManager } from '../../managers/MMOStateManager';
import { MMONetworkManager, MMOEvents } from '../../managers/MMONetworkManager';
import { MMOProgressionManager } from '../../managers/MMOProgressionManager';
import { EventBus } from '../../utils/EventBus';
import { I18n } from '../../i18n/I18nService';

// Theme
import {
  Colors, CSSColors, Fonts, Space, Depth,
  createPillButton, flashText,
} from '../../ui/mmo/GameUITheme';

// UI Components
import { MMOLeftSidebar } from '../../ui/mmo/MMOLeftSidebar';
// MMORightPanel removed — alliance actions moved to hamburger menu
import { EnhancedChatPanel } from '../../ui/mmo/EnhancedChatPanel';
import { EnhancedAllianceDialog } from '../../ui/mmo/EnhancedAllianceDialog';
import { TerritoryMapPanel } from '../../ui/mmo/TerritoryMapPanel';
import { DirectMessageDialog } from '../../ui/mmo/DirectMessageDialog';
import { BrowseAlliancesDialog } from '../../ui/mmo/BrowseAlliancesDialog';
import { MMOCombatLog } from '../../ui/mmo/MMOCombatLog';
import { CommandDock } from '../../ui/mmo/CommandDock';
import { HamburgerMenu } from '../../ui/mmo/HamburgerMenu';
import type { HamburgerNavButton } from '../../ui/mmo/HamburgerMenu';
import { MobileDOMOverlay } from '../../ui/mmo/MobileDOMOverlay';

// Phase 3 progression UI
// MMOResourceDisplay removed — resource categories not yet implemented
import { ShipProgressionPanel } from '../../ui/mmo/ShipProgressionPanel';
// PlayerProgressionPanel removed — leveling system no longer exists
import { ComponentUpgradeDialog } from '../../ui/mmo/ComponentUpgradeDialog';
import { MissionPanel } from '../../ui/mmo/MissionPanel';
import { ShipStoreDialog } from '../../ui/mmo/ShipStoreDialog';
import { ComponentMarketplaceDialog } from '../../ui/mmo/ComponentMarketplaceDialog';
import { CrewManagementDialog } from '../../ui/mmo/CrewManagementDialog';
import { StationManagementDialog } from '../../ui/mmo/StationManagementDialog';
import { MineralInventoryDialog } from '../../ui/mmo/MineralInventoryDialog';
import { AuctionHouseDialog } from '../../ui/mmo/AuctionHouseDialog';
import { WreckageDialog } from '../../ui/mmo/WreckageDialog';
import { InsuranceDialog } from '../../ui/mmo/InsuranceDialog';
import { ProfileDialog } from '../../ui/mmo/ProfileDialog';
import { EnemyProfileDialog } from '../../ui/mmo/EnemyProfileDialog';
import { EnemyShipDialog } from '../../ui/mmo/EnemyShipDialog';
import { RefundDialog } from '../../ui/mmo/RefundDialog';
import { AllianceHQDialog } from '../../ui/mmo/AllianceHQDialog';
import { RelicInventoryDialog } from '../../ui/mmo/RelicInventoryDialog';
import { OfflineRecapDialog } from '../../ui/mmo/OfflineRecapDialog';
import type { OfflineRecapEvent } from '../../managers/MMOStateManager';
import { TechListDialog } from '../../ui/mmo/TechListDialog';
import { ResearchBoardDialog } from '../../ui/mmo/ResearchBoardDialog';
import { BuyISDDialog } from '../../ui/mmo/BuyISDDialog';
import { WithdrawDialog } from '../../ui/mmo/WithdrawDialog';
import { ShipModificationDialog } from '../../ui/mmo/ShipModificationDialog';
import { ShipDesignDialog } from '../../ui/mmo/ShipDesignDialog';
import { TutorialHintPanel } from '../../ui/mmo/TutorialHintPanel';
import { HowToGuideDialog } from '../../ui/mmo/HowToGuideDialog';
import { AllianceLeaderboardDialog } from '../../ui/mmo/AllianceLeaderboardDialog';
import { isMobile, getResponsiveDialogSize } from '../../utils/ResponsiveHelper';
import { getSafeAreaInsets } from '../../utils/SafeArea';
import { ModeIndicatorBanner } from '../../ui/mmo/ModeIndicatorBanner';
import { ToastNotification } from '../../ui/mmo/ToastNotification';

// ── Top bar button definitions (data-driven) ───────────────────
interface NavButton {
  label: string;
  icon?: string;           // text/emoji fallback
  iconKey?: string;        // Phaser texture key for image sprite icon
  shortcut?: string;
  width?: number;
  colorKey?: string; // maps to NavColors in GameUITheme
  action: (scene: MMOHUDScene) => void;
}

// Single row: dark indigo buttons with colored text matching Figma design
// NAV_BUTTONS uses a function to get localized labels at render time
function getNavButtons(): NavButton[] {
  return [
    { label: I18n.t('nav.wreck'),   iconKey: 'icon-nav-wreck',   shortcut: 'R', width: 82, colorKey: 'wreck',   action: (s) => new WreckageDialog(s) },
    { label: I18n.t('nav.insure'),  icon: '🛡',                   shortcut: 'I', width: 79, colorKey: 'insure',  action: (s) => new InsuranceDialog(s) },
    { label: I18n.t('nav.trade'),   iconKey: 'icon-nav-trade',   shortcut: 'T', width: 79, colorKey: 'trade',   action: (s) => new AuctionHouseDialog(s) },
    { label: I18n.t('nav.ore'),     iconKey: 'icon-nav-ore',     shortcut: 'O', width: 62, colorKey: 'ore',     action: (s) => new MineralInventoryDialog(s) },
    { label: I18n.t('nav.crew'),    iconKey: 'icon-nav-crew',    shortcut: 'C', width: 73, colorKey: 'crew',    action: (s) => new CrewManagementDialog(s) },
    { label: I18n.t('nav.parts'),   iconKey: 'icon-nav-parts',   shortcut: 'P', width: 79, colorKey: 'parts',   action: (s) => new ComponentMarketplaceDialog(s) },
    { label: I18n.t('nav.shop'),    iconKey: 'icon-nav-shop',    shortcut: 'H', width: 71, colorKey: 'shop',    action: (s) => new ShipStoreDialog(s) },
    { label: I18n.t('nav.buy_isd'), icon: '💳',                                  width: 82, colorKey: 'shop',    action: (s) => new BuyISDDialog(s) },
    { label: I18n.t('nav.station'), iconKey: 'icon-nav-station', shortcut: 'B', width: 91, colorKey: 'station', action: (s) => new StationManagementDialog(s) },
    { label: I18n.t('nav.center'),  iconKey: 'icon-nav-center',                 width: 87, colorKey: 'center',  action: () => { EventBus.emit('center-on-player-ship'); } },
    { label: I18n.t('nav.follow'),  icon: '📍',                  shortcut: 'F', width: 82, colorKey: 'center',  action: () => { EventBus.emit('toggle-camera-follow'); } },
    { label: I18n.t('nav.research'), icon: '🔬',                  shortcut: 'U', width: 90, colorKey: 'station', action: (s) => new RelicInventoryDialog(s) },
    { label: I18n.t('nav.tech'),    icon: '🧬',                  shortcut: 'G', width: 68, colorKey: 'ore',     action: (s) => new TechListDialog(s) },
    { label: I18n.t('nav.alliance_hq'), icon: '🏛',                             width: 105, colorKey: 'station', action: (s) => new AllianceHQDialog(s) },
    { label: I18n.t('alliance.title'), icon: '⚔',                              width: 90, colorKey: 'station', action: () => EventBus.emit('mmo-open-alliance-dialog') },
    { label: I18n.t('alliance.browse'), icon: '🔍',                             width: 90, colorKey: 'station', action: () => EventBus.emit('mmo-browse-alliances') },
    { label: I18n.t('nav.leaderboard'), icon: '🏆',                  shortcut: 'L', width: 110, colorKey: 'hq',     action: () => EventBus.emit('mmo-open-leaderboard') },
    { label: I18n.t('alliance.message'), icon: '✉',                             width: 90, colorKey: 'crew',    action: () => EventBus.emit('mmo-open-dm-dialog') },
    { label: I18n.t('nav.refund'),  icon: '💰',                                  width: 82, colorKey: 'trade',   action: (s) => new RefundDialog(s) },
    { label: I18n.t('nav.withdraw'), icon: '💵',                  shortcut: 'W', width: 140, colorKey: 'trade',   action: (s) => new WithdrawDialog(s) },
    { label: I18n.t('nav.how_to'),  icon: '❓',                                  width: 82, colorKey: 'center',  action: (s) => new HowToGuideDialog(s, false) },
  ];
}

// Keyboard shortcut map (built lazily from getNavButtons)
let _shortcutMap: Record<string, NavButton> | null = null;
function getShortcutMap(): Record<string, NavButton> {
  if (!_shortcutMap) {
    _shortcutMap = {};
    getNavButtons().forEach((btn) => { if (btn.shortcut) _shortcutMap![btn.shortcut] = btn; });
  }
  return _shortcutMap;
}

// ── Top bar layout constants ───────────────────────────────────
const TOP_BAR_HEIGHT = 56;
const BTN_GAP = 4;
const MAX_RECONNECT_ATTEMPTS = 5; // must match MMONetworkManager

// ── Mobile mini-radar constants ────────────────────────────────
// TerritoryMapPanel is 243×243 at scale 1. A ~0.5 scale renders ~121×121 —
// roughly the footprint of the Earth planet sprite on mobile.
const MOBILE_RADAR_SCALE = 0.5;
const MOBILE_RADAR_RAW   = 243;
const MOBILE_RADAR_SIZE  = MOBILE_RADAR_RAW * MOBILE_RADAR_SCALE; // 121.5
const MOBILE_RADAR_MARGIN = 8;  // gap from screen edges
// Right-side FAB column = 52px wide, sits 8px from right edge → swallows
// the right-most ~60px. Push the radar that far inboard so it sits BESIDE
// the FAB column (Chat / Radar buttons) instead of underneath it.
const MOBILE_RADAR_FAB_CLEARANCE = 60;

export class MMOHUDScene extends Phaser.Scene {
  // Managers (public for button actions)
  public mmoState!: MMOStateManager;
  public mmoNetwork!: MMONetworkManager;
  // @ts-ignore - Reserved for future use
  private _mmoProgression!: MMOProgressionManager;

  // Mobile detection
  protected mobile = false;
  // When true, skip the Phaser-drawn top bar entirely (mobile HUD uses the
  // DOM overlay's top bar instead). Set by MMOHUDMobileScene subclass.
  protected skipPhaserTopBar = false;
  // Force a mobile layout regardless of isMobile() result. Used by the
  // mobile HUD scene so the mobile branch runs even on wide Chrome DevTools
  // windows that report pointer: fine.
  protected forceMobileLayout = false;

  // UI elements
  private topBar!: Phaser.GameObjects.Container;
  private topBarButtons: Phaser.GameObjects.Container[] = []; // kept as scene children (not nested in topBar) for reliable input
  // followButton removed — replaced with Withdraw in top bar
  private tickText!: Phaser.GameObjects.Text;
  private resourceText!: Phaser.GameObjects.Text;
  // @ts-ignore - Connection status now in left sidebar (hidden text for compatibility)
  private _connectionDot!: Phaser.GameObjects.Graphics;
  // @ts-ignore
  private _connectionText!: Phaser.GameObjects.Text;
  private prevResources: number = 0;

  // Sidebar panels
  private leftSidebar!: MMOLeftSidebar;
  // rightPanel removed — alliance actions in hamburger menu

  // Phase 3 progression UI
  // resourceDisplay removed
  private shipProgressionPanel!: ShipProgressionPanel;
  // playerProgressionPanel removed — leveling system no longer exists
  private componentUpgradeDialog!: ComponentUpgradeDialog;
  private missionPanel!: MissionPanel;

  // Bottom panels (tracked for responsive repositioning)
  private chatPanel!: EnhancedChatPanel;
  private territoryMap!: TerritoryMapPanel;
  private combatLog!: MMOCombatLog;

  // Disconnect overlay
  private disconnectOverlay: Phaser.GameObjects.Container | null = null;
  private disconnectStatusText: Phaser.GameObjects.Text | null = null;
  private disconnectButton: Phaser.GameObjects.Container | null = null;

  // Hamburger menu instance (null when closed)
  private hamburgerMenu: HamburgerMenu | null = null;
  
  // @ts-ignore used for lifecycle management
  private mobileOverlay: MobileDOMOverlay | null = null;

  // Mode indicator banner (mobile-friendly mode feedback)
  // @ts-ignore used for lifecycle management
  private modeBanner: ModeIndicatorBanner | null = null;

  // Mini-radar lift state: true while CommandDock is shown, so the radar sits
  // above the dock instead of being covered by it.
  private commandDockActive = false;

  // Keyboard shortcut keys
  private shortcutKeys: Phaser.Input.Keyboard.Key[] = [];
  private chatInputFocused = false;

  // Stored handler refs for proper EventBus cleanup
  private onChatFocused = (): void => { this.chatInputFocused = true; };
  private onChatUnfocused = (): void => { this.chatInputFocused = false; };

  constructor(config: string | Phaser.Types.Scenes.SettingsConfig = 'MMOHUDScene') {
    super(typeof config === 'string' ? { key: config } : config);
  }

  init(): void {
    this.mmoState = ServiceLocator.get<MMOStateManager>(Services.MMO_STATE);
    this.mmoNetwork = ServiceLocator.get<MMONetworkManager>(Services.MMO_NETWORK);
    this._mmoProgression = ServiceLocator.get<MMOProgressionManager>(Services.MMO_PROGRESSION);
  }

  create(): void {
    console.log('[MMOHUD] Creating HUD...');
    const { width, height } = this.cameras.main;
    this.mobile = this.forceMobileLayout || isMobile(this);

    // Ensure HUD scene is on top for input priority
    this.scene.bringToTop();

    // Build top bar (slides down from top) — unless the subclass opted out
    // (e.g. MMOHUDMobileScene, which uses the DOM overlay's top bar).
    if (!this.skipPhaserTopBar) {
      this.createTopBar(width);
      this.topBar.setY(-TOP_BAR_HEIGHT);
      this.tweens.add({ targets: this.topBar, y: 0, duration: 350, ease: 'Power2' });

      // Animate top bar buttons (kept as scene children, not in topBar)
      this.topBarButtons.forEach((btn, i) => {
        const targetY = btn.y;
        btn.setY(targetY - TOP_BAR_HEIGHT);
        this.tweens.add({ targets: btn, y: targetY, duration: 350, ease: 'Power2', delay: i * 15 });
      });
    }

    if (this.mobile) {
      // Mobile layout: every Phaser-drawn HUD element is hidden by default so
      // the gameplay area fills the screen. FABs in the DOM overlay toggle the
      // ones users need (chat, radar).
      this.leftSidebar = new MMOLeftSidebar(this, -260, 87, this.mmoState);
      this.leftSidebar.setVisible(false);

      // Chat panel lives off-screen until Chat FAB reveals it.
      this.chatPanel = new EnhancedChatPanel(this, 0, height, this.mmoState, this.mmoNetwork);
      this.chatPanel.setVisible(false);

      // Persistent mini-radar: scaled to ~0.5 (Earth-sized ≈121×121), hidden
      // legend, anchored bottom-right. Shifts up when CommandDock appears.
      this.territoryMap = new TerritoryMapPanel(this, -9999, -9999, this.mmoState);
      this.territoryMap.setScale(MOBILE_RADAR_SCALE);
      // Hide the legend so we only show the round radar disk.
      const legend = (this.territoryMap as any).legendContainer;
      if (legend) legend.setVisible(false);
      this.territoryMap.setVisible(true);
      this.positionMiniRadar(false);

      this.combatLog = new MMOCombatLog(this, -9999, -9999);
      this.combatLog.setVisible(false);

      // DOM overlay: hamburger top bar + FABs + drawer + sheets
      this.mobileOverlay = new MobileDOMOverlay(
        this,
        this.mmoState,
        {
          onBuyShip: () => new ShipStoreDialog(this),
          onToggleChat: () => {
            // Show chat panel from bottom; hide it again on second tap.
            const show = !this.chatPanel.visible;
            this.chatPanel.setVisible(show);
            if (show) {
              const bottom = getSafeAreaInsets().bottom;
              const chatH = (this.chatPanel as any).height ?? 320;
              // Leave room for the 44px top bar + 6px safe margin
              this.chatPanel.setPosition(0, Math.max(56, height - chatH - 20 - bottom));
            }
          },
          onToggleRadar: () => {
            // Mobile radar is persistent in the bottom-right corner.
            // The FAB toggles its visibility so players can dismiss it for
            // more gameplay real estate.
            const show = !this.territoryMap.visible;
            this.territoryMap.setVisible(show);
            if (show) this.positionMiniRadar(this.commandDockActive);
          },
          onNavAction: (key) => this.handleMobileNav(key),
        },
      );
    } else {
      // Desktop layout: full shark-fin panels
      // Left sidebar (slide in from left, 243px wide shark-fin panel)
      // Final position: 10px from left edge, 87px from top of browser
      this.leftSidebar = new MMOLeftSidebar(this, -260, 87, this.mmoState);
      this.leftSidebar.setExpandedX(10);
      this.tweens.add({ targets: this.leftSidebar, x: 10, duration: 400, ease: 'Power2', delay: 100 });

      // Communication panel (right side - chat + combat log merged, shark-fin bg)
      // 10px from right edge to match left sidebar's 10px from left edge
      this.chatPanel = new EnhancedChatPanel(this, width, TOP_BAR_HEIGHT + 25, this.mmoState, this.mmoNetwork);
      const chatExpandedX = width - 243 - 10;
      this.chatPanel.setExpandedX(chatExpandedX);
      this.tweens.add({
        targets: this.chatPanel, x: chatExpandedX, duration: 400, ease: 'Power2', delay: 200,
        onUpdate: () => this.chatPanel.updateClipMask(),
        onComplete: () => this.chatPanel.updateClipMask(),
      });

      // Territory minimap (below Communication panel, right-aligned with it)
      const commPanelX = width - 243 - 10;
      const minimapY = TOP_BAR_HEIGHT + 25 + 508 + 28; // below comm panel + 28px gap
      this.territoryMap = new TerritoryMapPanel(this, commPanelX, height + 243, this.mmoState);
      this.territoryMap.setLegendFromBottom(height - minimapY);
      this.tweens.add({ targets: this.territoryMap, y: minimapY, duration: 400, ease: 'Power2', delay: 250 });

      // Combat log instance (hidden — LOG tab is inside Communication panel)
      this.combatLog = new MMOCombatLog(this, -9999, -9999);
      this.combatLog.setVisible(false);
    }

    // ── Prominent "BUY A SHIP" button (desktop only) ──
    // On mobile the MobileDOMOverlay top bar already has a BUY SHIP button, so
    // showing the Phaser one too would duplicate the CTA and steal thumb space.
    if (!this.mobile) {
      this.createBuyAShipButton(width);
    }

    // Mode indicator banner (move / attack / mine targeting modes)
    // Works on both desktop and mobile; on mobile it's the primary mode signal
    // since there's no cursor change to rely on.
    this.modeBanner = new ModeIndicatorBanner(this);

    // Command dock (bottom center, slides up on unit selection)
    new CommandDock(this, this.mmoState);

    // FTUE tutorial hint panel (bottom center, above command dock)
    new TutorialHintPanel(this);

    // Phase 3 progression UI
    this.shipProgressionPanel = new ShipProgressionPanel(this);
    // PlayerProgressionPanel removed — leveling system no longer exists
    this.componentUpgradeDialog = new ComponentUpgradeDialog(this);
    this.missionPanel = new MissionPanel(this);

    // Bottom status bar
    // Bottom bar removed for cleaner UI

    // Keyboard shortcuts
    this.setupKeyboardShortcuts();

    // Responsive resize
    this.scale.on('resize', this.onResize, this);

    // Events
    this.setupEventListeners();

    // Deep-link: auto-open ship store if ?shop=true in URL
    try {
      const params = new URLSearchParams(window.location.search);
      if (params.get('shop') === 'true') {
        new ShipStoreDialog(this);
      }
    } catch (_e) { /* ignore URL parse errors */ }

    // Auto-open How To guide on first login
    if (!HowToGuideDialog.hasBeenSeen()) {
      this.time.delayedCall(600, () => new HowToGuideDialog(this, true));
    }

    // Request resources immediately — by the time the HUD is created,
    // the player is already authenticated and in the world
    EventBus.emit('send_message', { type: 'mmo_get_resources', payload: { worldId: 1 } });
    console.log('[MMOHUD] HUD created — resources requested');
  }

  // ── Responsive Resize ──────────────────────────────────────────

  private onResize(gameSize: Phaser.Structs.Size): void {
    const w = gameSize.width;
    const h = gameSize.height;

    if (this.mobile) {
      // Mobile: chat strip at bottom, offset by safe-area inset
      const safeBottom = getSafeAreaInsets().bottom;
      this.chatPanel?.setPosition(0, h - 40 - safeBottom);
      this.chatPanel?.updateClipMask();
      // Re-anchor the mini-radar on resize / rotation.
      this.positionMiniRadar(this.commandDockActive);
    } else {
      // Alliance card (bottom-left, 30px from bottom)
      // rightPanel removed

      // Communication panel (right side, shark-fin, 10px from right edge)
      this.chatPanel?.setPosition(w - 243 - 10, TOP_BAR_HEIGHT + 25);
      this.chatPanel?.updateClipMask();

      // Minimap (below Communication panel)
      const commX = w - 243 - 10;
      const mmY = TOP_BAR_HEIGHT + 25 + 508 + 28;
      this.territoryMap?.setPosition(commX, mmY);
      this.territoryMap?.setLegendFromBottom(h - mmY);
    }
  }

  // ── Top Bar ────────────────────────────────────────────────────

  private createTopBar(width: number): void {
    this.topBar = this.add.container(0, 0);

    // No background overlay -- buttons float directly over the starfield

    // ── Left: Logo text "CRIMSON\nMANDATE" (two-line, cyan, Orbitron Black) ──
    const logoSize = this.mobile ? '11px' : '18px';
    const logo = this.add.text(Space.lg, this.mobile ? 16 : 20, I18n.t('hud.logo'), {
      fontSize: logoSize,
      color: CSSColors.cyan,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: this.mobile ? 1 : 3,
      lineSpacing: -2,
      align: 'center',
    });
    logo.setOrigin(0, 0);
    this.topBar.add(logo);

    // ── Right: Profile icon (replaces exit/power icon) ──
    const profileBtn = this.createProfileIcon(width - 40, 24);
    profileBtn.setDepth(Depth.topBar);
    this.topBarButtons.push(profileBtn);

    // ── Center: Hamburger + Center + Follow quick buttons ──
    this.createTopBarQuickButtons();

    // Hidden resource/tick text (updated by events, shown in left panel later)
    this.resourceText = this.add.text(-100, -100, '0', { fontSize: '1px' });
    this.tickText = this.add.text(-100, -100, 'Turn 0', { fontSize: '1px' });
    this._connectionDot = this.add.graphics();
    this._connectionDot.setPosition(-100, -100);  // Move off-screen to avoid artifact
    this._connectionDot.setVisible(false);
    this._connectionText = this.add.text(-100, -100, '', { fontSize: '1px' });

    this.topBar.setDepth(Depth.topBar);
  }

  private createTopBarQuickButtons(): void {
    const y = 24;
    const mobile = this.mobile;
    // On mobile: start closer to left edge, use shorter button widths
    let x = mobile ? 90 : 190;
    const btnH = 24;

    // ☰ Hamburger button
    const menuW = mobile ? 44 : 80;
    const menuLabel = mobile ? '\u2630' : I18n.t('hud.menu');
    const hamburgerBtn = createPillButton(this, x, y, menuLabel, {
      colorKey: 'wreck',
      width: menuW,
      height: btnH,
      onClick: () => this.toggleHamburgerMenu(),
      depth: Depth.topBar,
    });
    this.topBarButtons.push(hamburgerBtn);
    x += menuW + BTN_GAP;

    // Center button (high-frequency)
    const navBtns = getNavButtons();
    const centerDef = navBtns.find((b) => b.label === I18n.t('nav.center'))!;
    const centerW = mobile ? 50 : (centerDef.width ?? 87);
    const centerLabel = mobile ? '\u2316' : centerDef.label;
    const centerBtn = createPillButton(this, x, y, centerLabel, {
      icon: mobile ? undefined : centerDef.icon,
      iconKey: mobile ? undefined : centerDef.iconKey,
      colorKey: centerDef.colorKey,
      width: centerW,
      height: btnH,
      onClick: () => centerDef.action(this),
      depth: Depth.topBar,
    });
    this.topBarButtons.push(centerBtn);
    x += centerW + BTN_GAP;

    // Withdraw button (top bar quick access)
    const withdrawDef = navBtns.find((b) => b.label === I18n.t('nav.withdraw'))!;
    const withdrawW = mobile ? 50 : (withdrawDef.width ?? 90);
    const withdrawLabel = mobile ? '💵' : withdrawDef.label;
    const withdrawBtn = createPillButton(this, x, y, withdrawLabel, {
      icon: mobile ? undefined : withdrawDef.icon,
      iconKey: mobile ? undefined : withdrawDef.iconKey,
      colorKey: withdrawDef.colorKey,
      width: withdrawW,
      height: btnH,
      onClick: () => withdrawDef.action(this),
      depth: Depth.topBar,
      labelOffsetX: 12,
    });
    this.topBarButtons.push(withdrawBtn);
    x += withdrawW + BTN_GAP;

    // Bug report button
    const bugBtnW = mobile ? 38 : 48;
    const bugBtn = createPillButton(this, x, y, '🐛', {
      colorKey: 'wreck',
      width: bugBtnW,
      height: btnH,
      onClick: () => this.openBugReportOverlay(),
      depth: Depth.topBar,
    });
    this.topBarButtons.push(bugBtn);

    // Mobile: add Station button as quick access (since left sidebar is hidden)
    if (mobile) {
      x += bugBtnW + BTN_GAP;
      const stationDef = navBtns.find((b) => b.label === I18n.t('nav.station'))!;
      const stationBtn = createPillButton(this, x, y, '\uD83D\uDEF0', {
        colorKey: 'station',
        width: 44,
        height: btnH,
        onClick: () => stationDef.action(this),
        depth: Depth.topBar,
      });
      this.topBarButtons.push(stationBtn);
    }

    // Camera follow is still togglable via F key / hamburger menu
  }

  /**
   * Creates a prominent "BUY A SHIP" call-to-action button at top-center of the HUD.
   * Styled with a glowing cyan border and a subtle pulsing animation to draw attention.
   */
  private createBuyAShipButton(screenWidth: number): void {
    const btnW = 180;
    const btnH = 36;
    const x = screenWidth / 2;
    const y = TOP_BAR_HEIGHT + 18; // just below the nav bar

    const container = this.add.container(x, y);
    container.setDepth(Depth.topBar + 1);

    // Dark background with rounded-rect appearance
    const bg = this.add.graphics();
    const drawBg = (borderAlpha: number, borderWidth: number) => {
      bg.clear();
      // Fill
      bg.fillStyle(0x0A1929, 0.95);
      bg.fillRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
      // Cyan border glow
      bg.lineStyle(borderWidth, Colors.cyan, borderAlpha);
      bg.strokeRoundedRect(-btnW / 2, -btnH / 2, btnW, btnH, 8);
    };
    drawBg(0.6, 2);
    container.add(bg);

    // Label text
    const label = this.add.text(0, 0, 'BUY A SHIP', {
      fontSize: '15px',
      color: CSSColors.cyan,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      letterSpacing: 2,
      align: 'center',
    });
    label.setOrigin(0.5);
    container.add(label);

    // Hit zone added directly to scene (NOT container) for reliable input
    const hitZone = this.add.zone(x, y, btnW, btnH);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(-btnW / 2, -btnH / 2, btnW, btnH), Phaser.Geom.Rectangle.Contains);
    hitZone.setDepth(Depth.topBar + 2);

    // Hover effects
    hitZone.on('pointerover', () => {
      drawBg(0.9, 2.5);
      label.setColor('#ffffff');
      this.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      drawBg(0.6, 2);
      label.setColor(CSSColors.cyan);
      this.input.setDefaultCursor('default');
    });

    // Click -> open Ship Store
    hitZone.on('pointerdown', () => {
      new ShipStoreDialog(this);
    });

    // Pulsing glow animation: subtly breathes the border alpha
    this.tweens.add({
      targets: { val: 0 },
      val: 1,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      onUpdate: (_tween: Phaser.Tweens.Tween, target: { val: number }) => {
        const alpha = 0.35 + target.val * 0.45; // pulses between 0.35 and 0.80
        drawBg(alpha, 1.5 + target.val * 1);
      },
    });

    // Slide-in animation (matches top bar entrance)
    container.setAlpha(0);
    container.setY(y - 20);
    this.tweens.add({
      targets: container,
      y,
      alpha: 1,
      duration: 400,
      ease: 'Power2',
      delay: 300,
    });
  }

  public toggleHamburgerMenu(): void {
    if (this.hamburgerMenu) {
      // Close existing menu — the dialog will destroy itself via closeDialog
      this.hamburgerMenu = null;
      return;
    }
    // Build the nav button list for the menu (exclude Center/Withdraw — they're in the top bar)
    const navButtons: HamburgerNavButton[] = getNavButtons()
      .filter((def) => def.label !== I18n.t('nav.center') && def.label !== I18n.t('nav.withdraw'))
      .map((def) => ({
        label: def.label,
        icon: def.icon,
        iconKey: def.iconKey,
        colorKey: def.colorKey,
        width: def.width,
        action: (scene: Phaser.Scene) => def.action(scene as MMOHUDScene),
      }));
    this.hamburgerMenu = new HamburgerMenu(this, navButtons);
  }

  private createProfileIcon(x: number, y: number): Phaser.GameObjects.Container {
    const size = 30; // 25% larger than original 24
    const container = this.add.container(x, y);

    // Circle background (#131F26) with cyan stroke at 15% opacity
    const bg = this.add.graphics();
    bg.fillStyle(0x131F26, 1.0);
    bg.fillCircle(size / 2, size / 2, size / 2);
    bg.lineStyle(1, Colors.cyan, 0.15);
    bg.strokeCircle(size / 2, size / 2, size / 2);

    // Person silhouette icon from Figma
    let icon: Phaser.GameObjects.Image | Phaser.GameObjects.Text;
    if (this.textures.exists('icon-nav-profile')) {
      icon = this.add.image(size / 2, size / 2, 'icon-nav-profile');
      (icon as Phaser.GameObjects.Image).setDisplaySize(25, 25);
    } else {
      // Fallback text icon
      icon = this.add.text(size / 2, size / 2, '👤', { fontSize: '18px', color: '#ffffff' });
      (icon as Phaser.GameObjects.Text).setOrigin(0.5);
    }
    icon.setOrigin(0.5);

    const hitZone = this.add.zone(0, 0, size, size).setOrigin(0, 0);
    hitZone.setInteractive(new Phaser.Geom.Rectangle(0, 0, size, size), Phaser.Geom.Rectangle.Contains);
    container.add([bg, icon, hitZone]);

    hitZone.on('pointerover', () => {
      bg.clear();
      bg.fillStyle(0x131F26, 1.0);
      bg.fillCircle(size / 2, size / 2, size / 2);
      bg.lineStyle(1.5, Colors.cyan, 0.5);
      bg.strokeCircle(size / 2, size / 2, size / 2);
      this.input.setDefaultCursor('pointer');
    });
    hitZone.on('pointerout', () => {
      bg.clear();
      bg.fillStyle(0x131F26, 1.0);
      bg.fillCircle(size / 2, size / 2, size / 2);
      bg.lineStyle(1, Colors.cyan, 0.15);
      bg.strokeCircle(size / 2, size / 2, size / 2);
      this.input.setDefaultCursor('default');
    });
    hitZone.on('pointerdown', () => {
      // Profile icon click — open profile dialog
      EventBus.emit('open-profile-dialog');
    });

    return container;
  }

  // Bottom bar removed for cleaner UI

  // ── Bug Report Overlay ─────────────────────────────────────────

  public openBugReportOverlay(): void {
    // Collect game state
    const state = this.mmoState;
    const myUnits = state.getPlayerUnits();
    const fps = Math.round(this.game.loop.actualFps);
    const tick = state.getTick();
    const userId = state.getUserId();
    const playerPos = state.getPlayerPosition();

    const gameState: Record<string, unknown> = {
      userId,
      tick,
      fps,
      unitCount: myUnits.length,
    };
    if (playerPos) {
      gameState.position = playerPos;
    }

    // Build overlay DOM
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:99999',
      'background:rgba(0,0,8,0.88)', 'backdrop-filter:blur(4px)',
      '-webkit-backdrop-filter:blur(4px)',
      'display:flex', 'align-items:center', 'justify-content:center',
    ].join(';');

    const panel = document.createElement('div');
    panel.style.cssText = [
      'background:linear-gradient(135deg,#0d0d1f 0%,#1a1a33 100%)',
      'border:1px solid rgba(255,102,0,0.45)',
      'max-width:460px', 'width:90%', 'padding:28px 24px',
      'position:relative',
      'box-shadow:0 0 40px rgba(255,102,0,0.15)',
      'font-family:"Share Tech Mono",monospace',
    ].join(';');

    const closeBtn = document.createElement('button');
    closeBtn.textContent = '\u00D7';
    closeBtn.style.cssText = 'position:absolute;top:10px;right:14px;background:none;border:none;color:rgba(0,255,255,0.5);font-size:22px;cursor:pointer;line-height:1;padding:0;';
    const close = () => { if (overlay.parentNode) overlay.parentNode.removeChild(overlay); };
    closeBtn.addEventListener('click', close);
    overlay.addEventListener('click', (e) => { if (e.target === overlay) close(); });

    const title = document.createElement('h2');
    title.textContent = 'Report a Bug';
    title.style.cssText = 'font-family:"Orbitron",sans-serif;font-size:15px;font-weight:700;text-transform:uppercase;letter-spacing:2px;color:#ff6600;text-shadow:0 0 8px rgba(255,102,0,0.4);margin:0 0 4px;';

    const subtitle = document.createElement('p');
    subtitle.textContent = 'Game state will be collected automatically.';
    subtitle.style.cssText = 'font-size:11px;color:rgba(0,255,255,0.5);margin:0 0 18px;';

    const stateInfo = document.createElement('div');
    stateInfo.style.cssText = 'background:rgba(0,0,8,0.5);border:1px solid rgba(0,255,255,0.1);padding:8px 10px;margin-bottom:16px;font-size:11px;color:rgba(0,255,255,0.55);line-height:1.6;';
    stateInfo.textContent = `Tick: ${tick} | FPS: ${fps} | Units: ${myUnits.length}${playerPos ? ` | Pos: ${playerPos.q},${playerPos.r}` : ''}`;

    const descLabel = document.createElement('label');
    descLabel.textContent = 'Describe the bug *';
    descLabel.style.cssText = 'display:block;font-size:11px;text-transform:uppercase;letter-spacing:1px;color:rgba(0,255,255,0.5);margin-bottom:6px;';

    const textarea = document.createElement('textarea');
    textarea.placeholder = 'What happened? What were you trying to do? What did you expect?';
    textarea.rows = 5;
    textarea.style.cssText = [
      'width:100%', 'box-sizing:border-box',
      'background:#0d0d1f', 'border:1px solid rgba(0,255,255,0.2)',
      'color:#00ffff', 'padding:9px 11px',
      'font-family:"Share Tech Mono",monospace', 'font-size:12px',
      'outline:none', 'resize:vertical', 'margin-bottom:14px',
    ].join(';');

    // Screenshot upload section
    const screenshotLabel = document.createElement('label');
    screenshotLabel.textContent = 'SCREENSHOTS (optional, up to 3)';
    screenshotLabel.style.cssText = 'display:block;font-size:10px;text-transform:uppercase;letter-spacing:1px;color:rgba(0,255,255,0.5);margin-bottom:6px;';

    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.accept = 'image/png,image/jpeg,image/gif,image/webp';
    fileInput.multiple = true;
    fileInput.style.cssText = 'width:100%;box-sizing:border-box;background:#0d0d1f;border:1px solid rgba(0,255,255,0.2);color:#00ffff;padding:8px;font-size:11px;margin-bottom:6px;';

    const previewContainer = document.createElement('div');
    previewContainer.style.cssText = 'display:flex;gap:8px;margin-bottom:14px;flex-wrap:wrap;';

    fileInput.addEventListener('change', () => {
      previewContainer.innerHTML = '';
      const files = fileInput.files;
      if (!files) return;
      const maxFiles = Math.min(files.length, 3);
      for (let i = 0; i < maxFiles; i++) {
        const img = document.createElement('img');
        img.src = URL.createObjectURL(files[i]);
        img.style.cssText = 'width:80px;height:60px;object-fit:cover;border:1px solid rgba(0,255,255,0.3);border-radius:2px;';
        previewContainer.appendChild(img);
      }
      if (files.length > 3) {
        const warn = document.createElement('span');
        warn.textContent = 'Max 3 files';
        warn.style.cssText = 'color:#ff6600;font-size:10px;align-self:center;';
        previewContainer.appendChild(warn);
      }
    });

    const errorDiv = document.createElement('div');
    errorDiv.style.cssText = 'display:none;color:#ff0055;font-size:12px;margin-bottom:10px;';

    const successDiv = document.createElement('div');
    successDiv.style.cssText = 'display:none;text-align:center;padding:20px 0;';
    successDiv.innerHTML = '<div style="font-size:28px;margin-bottom:8px;">&#10003;</div><p style="font-family:Orbitron,sans-serif;font-size:13px;color:#00ff41;">Bug report submitted!</p><p style="font-size:11px;color:rgba(0,255,255,0.5);margin-top:6px;">Thank you for helping improve the game.</p>';

    const submitBtn = document.createElement('button');
    submitBtn.textContent = 'Submit Report';
    submitBtn.style.cssText = [
      'font-family:"Orbitron",sans-serif', 'font-weight:700',
      'font-size:12px', 'text-transform:uppercase', 'letter-spacing:2px',
      'padding:11px 24px', 'width:100%', 'cursor:pointer',
      'background:linear-gradient(180deg,#cc5500 0%,#8b3300 100%)',
      'border:1px solid #ff6600', 'color:#fff', 'transition:opacity 0.2s',
    ].join(';');

    submitBtn.addEventListener('click', () => {
      const description = textarea.value.trim();
      if (description.length < 5) {
        errorDiv.textContent = 'Please describe the bug (at least 5 characters).';
        errorDiv.style.display = 'block';
        return;
      }
      errorDiv.style.display = 'none';
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting...';

      // Build FormData for multipart upload (supports screenshots)
      const formData = new FormData();
      formData.append('description', description);
      formData.append('source', 'game');
      formData.append('title', `In-game report (tick ${tick})`);
      formData.append('gameState', JSON.stringify(gameState));

      // Attach up to 3 screenshots
      if (fileInput.files) {
        const maxFiles = Math.min(fileInput.files.length, 3);
        for (let i = 0; i < maxFiles; i++) {
          formData.append('screenshots', fileInput.files[i]);
        }
      }

      // Get auth token for the request
      const token = localStorage.getItem('crimsonmandate_token');
      const headers: Record<string, string> = {};
      if (token) headers['Authorization'] = `Bearer ${token}`;

      fetch('/api/bugs/user-report', {
        method: 'POST',
        headers,
        body: formData,
      })
        .then((r) => r.json())
        .then((data: { success?: boolean; error?: string }) => {
          if (data.success) {
            textarea.style.display = 'none';
            descLabel.style.display = 'none';
            screenshotLabel.style.display = 'none';
            fileInput.style.display = 'none';
            previewContainer.style.display = 'none';
            submitBtn.style.display = 'none';
            stateInfo.style.display = 'none';
            successDiv.style.display = 'block';
            setTimeout(close, 3000);
          } else {
            errorDiv.textContent = data.error ?? 'Failed to submit. Please try again.';
            errorDiv.style.display = 'block';
            submitBtn.disabled = false;
            submitBtn.textContent = 'Submit Report';
          }
        })
        .catch(() => {
          errorDiv.textContent = 'Network error. Please try again.';
          errorDiv.style.display = 'block';
          submitBtn.disabled = false;
          submitBtn.textContent = 'Submit Report';
        });
    });

    panel.appendChild(closeBtn);
    panel.appendChild(title);
    panel.appendChild(subtitle);
    panel.appendChild(stateInfo);
    panel.appendChild(descLabel);
    panel.appendChild(textarea);
    panel.appendChild(screenshotLabel);
    panel.appendChild(fileInput);
    panel.appendChild(previewContainer);
    panel.appendChild(errorDiv);
    panel.appendChild(successDiv);
    panel.appendChild(submitBtn);
    overlay.appendChild(panel);
    document.body.appendChild(overlay);

    // Make the overlay scrollable so the soft keyboard doesn't cover the submit button.
    overlay.style.overflowY = 'auto';
    overlay.style.setProperty('-webkit-overflow-scrolling', 'touch');

    // On iOS the soft keyboard covers the lower half of the screen when the textarea
    // is focused — scroll the panel into view so the submit button stays reachable.
    textarea.addEventListener('focus', () => {
      try {
        textarea.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch (_e) {
        // scrollIntoView options unsupported on some older browsers — fall back
        textarea.scrollIntoView();
      }
    });

    // Focus textarea after a brief delay
    setTimeout(() => textarea.focus(), 50);
  }

  // ── Keyboard Shortcuts ─────────────────────────────────────────

  private setupKeyboardShortcuts(): void {
    if (!this.input.keyboard) return;

    // Listen for chat input focus/unfocus to suppress shortcuts
    EventBus.on('chat-input-focused', this.onChatFocused, this);
    EventBus.on('chat-input-unfocused', this.onChatUnfocused, this);

    // Register letter shortcuts (only when no input is focused)
    const shortcutMap = getShortcutMap();
    Object.keys(shortcutMap).forEach((key) => {
      const keyCode = Phaser.Input.Keyboard.KeyCodes[key as keyof typeof Phaser.Input.Keyboard.KeyCodes];
      if (keyCode) {
        const keyObj = this.input.keyboard!.addKey(keyCode, false);
        keyObj.on('down', () => {
          // Don't trigger if a text input (HTML or Phaser chat) is focused
          if (this.chatInputFocused) return;
          if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
          shortcutMap[key].action(this);
        });
        this.shortcutKeys.push(keyObj);
      }
    });

    // M key to toggle hamburger menu
    const mKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.M, false);
    mKey.on('down', () => {
      if (this.chatInputFocused) return;
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') return;
      this.toggleHamburgerMenu();
    });
    this.shortcutKeys.push(mKey);

    // Global ESC key - close topmost dialog/modal
    const escKey = this.input.keyboard.addKey(Phaser.Input.Keyboard.KeyCodes.ESC, false);
    escKey.on('down', () => {
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        (document.activeElement as HTMLElement).blur();
        return;
      }
      // Emit global close event for Phase 3 panels and any tracked dialogs
      // (createDialogChrome-based dialogs already handle ESC internally)
      EventBus.emit('close-topmost-dialog');
    });
  }

  // ── Event Listeners ────────────────────────────────────────────

  private setupEventListeners(): void {
    EventBus.on(MMOEvents.TICK_UPDATE, this.onTickUpdate, this);
    EventBus.on(MMOEvents.DISCONNECTED, this.onDisconnected, this);
    EventBus.on(MMOEvents.CONNECTED, this.onConnected, this);
    EventBus.on(MMOEvents.RECONNECT_FAILED, this.onReconnectFailed, this);
    EventBus.on(MMOEvents.ERROR, this.onNetworkError, this);
    EventBus.on('mmo-command-dock-shown', this.onCommandDockShown, this);
    EventBus.on('mmo-command-dock-hidden', this.onCommandDockHidden, this);
    EventBus.on('mmo-hex-clicked', this.onHexClicked, this);
    EventBus.on('mmo-open-alliance-dialog', this.onOpenAllianceDialog, this);
    EventBus.on('mmo-open-dm-dialog', this.onOpenDMDialog, this);
    EventBus.on('mmo-browse-alliances', this.onBrowseAlliances, this);
    EventBus.on('mmo-open-leaderboard', this.onOpenLeaderboard, this);
    EventBus.on('mmo-alliance-updated', this.onAllianceUpdated, this);
    EventBus.on('open_ship_progression', this.onOpenShipProgression, this);
    EventBus.on('open_component_upgrade', this.onOpenComponentUpgrade, this);
    EventBus.on('mmo-open-component-store', this.onOpenComponentStore, this);
    EventBus.on('open-component-store', this.onOpenComponentStore, this);
    EventBus.on('open_mission_panel', this.onOpenMissionPanel, this);
    // toggle_resource_display removed
    EventBus.on(MMOEvents.WORLD_JOINED, this.onWorldJoined, this);
    EventBus.on('open-station-management', this.onOpenStationManagement, this);
    EventBus.on('mmo_player_resources', this.onPlayerResources, this);
    EventBus.on('open-profile-dialog', this.onOpenProfileDialog, this);
    EventBus.on('open-enemy-profile', this.onOpenEnemyProfile, this);
    EventBus.on('open-enemy-ship', this.onOpenEnemyShip, this);
    EventBus.on('open-wreckage-dialog', this.onOpenWreckageDialog, this);
    EventBus.on('open-crew-management', this.onOpenCrewManagement, this);
    EventBus.on('open-insurance-dialog', this.onOpenInsuranceDialog, this);
    EventBus.on('show-offline-recap', this.onShowOfflineRecap, this);
    EventBus.on('open_ship_modification', this.onOpenShipModification, this);
    // open-station-buy-ship listener removed — ships are USDC/USDT only via Ship Store
    EventBus.on('open-station-design', this.onOpenStationDesign, this);
    EventBus.on('open-station-research', this.onOpenStationResearch, this);
    EventBus.on('open-station-tech', this.onOpenStationTech, this);
  }

  private removeEventListeners(): void {
    EventBus.off(MMOEvents.TICK_UPDATE, this.onTickUpdate, this);
    EventBus.off(MMOEvents.DISCONNECTED, this.onDisconnected, this);
    EventBus.off(MMOEvents.CONNECTED, this.onConnected, this);
    EventBus.off(MMOEvents.RECONNECT_FAILED, this.onReconnectFailed, this);
    EventBus.off(MMOEvents.ERROR, this.onNetworkError, this);
    EventBus.off('mmo-command-dock-shown', this.onCommandDockShown, this);
    EventBus.off('mmo-command-dock-hidden', this.onCommandDockHidden, this);
    EventBus.off('mmo-hex-clicked', this.onHexClicked, this);
    EventBus.off('mmo-open-alliance-dialog', this.onOpenAllianceDialog, this);
    EventBus.off('mmo-open-dm-dialog', this.onOpenDMDialog, this);
    EventBus.off('mmo-browse-alliances', this.onBrowseAlliances, this);
    EventBus.off('mmo-open-leaderboard', this.onOpenLeaderboard, this);
    EventBus.off('mmo-alliance-updated', this.onAllianceUpdated, this);
    EventBus.off('open_ship_progression', this.onOpenShipProgression, this);
    EventBus.off('open_component_upgrade', this.onOpenComponentUpgrade, this);
    EventBus.off('mmo-open-component-store', this.onOpenComponentStore, this);
    EventBus.off('open-component-store', this.onOpenComponentStore, this);
    EventBus.off('open_mission_panel', this.onOpenMissionPanel, this);
    // toggle_resource_display removed
    EventBus.off(MMOEvents.WORLD_JOINED, this.onWorldJoined, this);
    EventBus.off('open-station-management', this.onOpenStationManagement, this);
    EventBus.off('mmo_player_resources', this.onPlayerResources, this);
    EventBus.off('open-profile-dialog', this.onOpenProfileDialog, this);
    EventBus.off('open-enemy-profile', this.onOpenEnemyProfile, this);
    EventBus.off('open-enemy-ship', this.onOpenEnemyShip, this);
    EventBus.off('open-wreckage-dialog', this.onOpenWreckageDialog, this);
    EventBus.off('open-crew-management', this.onOpenCrewManagement, this);
    EventBus.off('open-insurance-dialog', this.onOpenInsuranceDialog, this);
    EventBus.off('show-offline-recap', this.onShowOfflineRecap, this);
    EventBus.off('open_ship_modification', this.onOpenShipModification, this);
    // open-station-buy-ship listener removed
    EventBus.off('open-station-design', this.onOpenStationDesign, this);
    EventBus.off('open-station-research', this.onOpenStationResearch, this);
    EventBus.off('open-station-tech', this.onOpenStationTech, this);
    // camera-follow-changed listener removed (Follow button replaced with Withdraw)
  }

  // ── Event Handlers ─────────────────────────────────────────────

  private onTickUpdate(payload: any): void {
    if (payload && payload.tick) {
      this.mmoState.setTick(payload.tick);
      this.tickText?.setText(`Turn ${payload.tick}`);
    }

    const resources = this.mmoState.getResources();

    // Flash resource text when value changes (only if Phaser top bar is drawn)
    if (resources !== this.prevResources && this.prevResources !== 0 && this.resourceText) {
      const color = resources > this.prevResources ? CSSColors.green : CSSColors.red;
      flashText(this, this.resourceText, color);
    }
    this.prevResources = resources;

    this.resourceText?.setText(resources.toString());
    this.leftSidebar?.updateDisplay();
    // rightPanel removed
  }

  private onDisconnected(): void {
    // Route to left sidebar connection indicator
    this.leftSidebar?.setConnectionStatus(false);
    // Show disconnect overlay
    this.showDisconnectOverlay();
  }

  private onConnected(): void {
    this.leftSidebar?.setConnectionStatus(true);
    // Dismiss disconnect overlay
    this.hideDisconnectOverlay();
  }

  private onReconnectFailed(): void {
    if (this.disconnectStatusText) {
      this.disconnectStatusText.setText(I18n.t('disconnect.failed'));
    }
    this.disconnectButton?.setVisible(true);
  }

  // ── Command-dock / mobile overlay coordination ───────────────────
  // Bottom-of-screen on mobile is crowded: command dock (144px tall) overlaps
  // the collapsed chat strip (40px at `height - 40`). When the dock appears,
  // push the chat strip above the dock. When it hides, restore.

  private onCommandDockShown(payload: { dockTopY: number; dockH: number }): void {
    if (!this.mobile) return;
    // The dock may appear either (a) at the bottom of the screen (legacy, dockH > 0)
    // or (b) as a left-side vertical sidebar / left-anchored info panel (dockH === 0).
    // Only lift bottom-anchored HUD elements (chat strip, FAB columns, mini-radar) when
    // the dock is actually at the bottom and would cover them. For left-side panels,
    // the bottom-right radar and bottom chat strip are never covered, so leave them
    // put to avoid pointless motion.
    const bottomDock = payload.dockH > 0;
    if (bottomDock) {
      const stripH = 40;
      const targetY = payload.dockTopY - stripH - 4;
      this.chatPanel?.setPosition(0, Math.max(80, targetY));
      this.chatPanel?.updateClipMask?.();
      // Also lift the DOM FAB columns so CHAT/RADAR/FLEET stay tappable above the dock.
      document.querySelectorAll<HTMLElement>('.m-fab-container, .m-fab-container-left')
        .forEach((el) => { el.style.transform = `translateY(-${payload.dockH}px)`; });
    }
    // Track state so onCommandDockHidden resets consistently.
    this.commandDockActive = true;
    this.positionMiniRadar(bottomDock);
  }

  private onCommandDockHidden(): void {
    if (!this.mobile) return;
    const h = this.cameras.main.height;
    const safeBottom = getSafeAreaInsets().bottom;
    this.chatPanel?.setPosition(0, h - 40 - safeBottom);
    this.chatPanel?.updateClipMask?.();
    document.querySelectorAll<HTMLElement>('.m-fab-container, .m-fab-container-left')
      .forEach((el) => { el.style.transform = ''; });
    // Drop the mini-radar back to its resting bottom-right corner.
    this.commandDockActive = false;
    this.positionMiniRadar(false);
  }

  /**
   * Position the persistent mini-radar in the bottom-right area.
   *
   * Resting position: bottom-right corner, but shifted LEFT of the right
   * FAB column (Chat/Radar buttons) so the disk and the FAB icons no longer
   * overlap. The `_lifted` flag is retained for signature compatibility but
   * is intentionally ignored — the legacy lift was tied to the bottom-anchored
   * CommandDock, which has been replaced by a left-side sidebar that never
   * covers the radar. Lifting the radar on every ship-select made it bob up
   * and down for no gain, so we keep it stationary now.
   */
  private positionMiniRadar(_lifted: boolean): void {
    if (!this.mobile || !this.territoryMap) return;
    const { width, height } = this.cameras.main;
    const safeBottom = getSafeAreaInsets().bottom;
    const baseBottom = MOBILE_RADAR_MARGIN + safeBottom;
    // Slide the radar left of the FAB column instead of stacking under it.
    const targetX = Math.round(
      width - MOBILE_RADAR_SIZE - MOBILE_RADAR_MARGIN - MOBILE_RADAR_FAB_CLEARANCE,
    );
    const targetY = Math.round(height - MOBILE_RADAR_SIZE - baseBottom);

    // Snap to the new position without a tween — preventing the "bobbing"
    // animation the user reported. A tween here would animate every time
    // the dock shows/hides since this method is still called on those
    // transitions for resize-safety.
    this.territoryMap.setPosition(targetX, targetY);
  }

  // ── Disconnect Overlay ───────────────────────────────────────────

  private showDisconnectOverlay(): void {
    if (this.disconnectOverlay) return; // already showing

    const { width, height } = this.cameras.main;
    const reason = this.mmoNetwork.disconnectReason;

    this.disconnectOverlay = this.add.container(0, 0);
    this.disconnectOverlay.setDepth(Depth.tooltip + 100); // above everything

    // Dark background
    const bg = this.add.rectangle(width / 2, height / 2, width, height, 0x0D0D1F, 0.85);
    bg.setInteractive(); // block clicks through to game
    this.disconnectOverlay.add(bg);

    // Panel — responsive: fills narrow viewports with 8px margin, capped to 400x220 on desktop
    const dims = getResponsiveDialogSize(400, 220, this);
    const panelW = dims.width;
    const panelH = dims.height;
    const panelX = width / 2;
    const panelY = height / 2;

    const panel = this.add.rectangle(panelX, panelY, panelW, panelH, 0x131F26, 1);
    panel.setStrokeStyle(1, Colors.cyan, 0.3);
    this.disconnectOverlay.add(panel);

    // Title
    const title = this.add.text(panelX, panelY - 60, I18n.t('disconnect.title'), {
      fontSize: '24px',
      color: CSSColors.cyan,
      fontFamily: Fonts.heading,
      fontStyle: '900',
      align: 'center',
    });
    title.setOrigin(0.5);
    this.disconnectOverlay.add(title);

    // Status text
    let statusMsg: string;
    if (reason === 'idle') {
      statusMsg = I18n.t('disconnect.idle');
    } else {
      const attempt = this.mmoNetwork.getReconnectAttempts();
      statusMsg = I18n.t('disconnect.reconnecting', { attempt: String(attempt), max: String(MAX_RECONNECT_ATTEMPTS) });
    }

    this.disconnectStatusText = this.add.text(panelX, panelY, statusMsg, {
      fontSize: '16px',
      color: CSSColors.textLight,
      fontFamily: Fonts.body,
      align: 'center',
    });
    this.disconnectStatusText.setOrigin(0.5);
    this.disconnectOverlay.add(this.disconnectStatusText);

    // Reconnect button
    this.disconnectButton = createPillButton(this, panelX - 60, panelY + 50, I18n.t('disconnect.reconnect'), {
      accentColor: Colors.cyan,
      width: 120,
      height: 32,
      onClick: () => {
        this.mmoNetwork.reconnect();
        if (this.disconnectStatusText) {
          this.disconnectStatusText.setText(I18n.t('disconnect.reconnecting', { attempt: '0', max: String(MAX_RECONNECT_ATTEMPTS) }));
        }
        this.disconnectButton?.setVisible(false);
      },
      depth: Depth.tooltip + 101,
    });
    // Show button immediately for idle, hide during auto-reconnect
    this.disconnectButton.setVisible(reason === 'idle');
    this.disconnectOverlay.add(this.disconnectButton);

    // Block keyboard shortcuts while overlay is visible
    this.chatInputFocused = true;

    // For network disconnects, update status text on each reconnect attempt
    if (reason === 'network') {
      this.time.addEvent({
        delay: 1000,
        loop: true,
        callback: () => {
          if (!this.disconnectOverlay || !this.disconnectStatusText) return;
          if (this.mmoNetwork.isConnected) return;
          const attempt = this.mmoNetwork.getReconnectAttempts();
          if (attempt > 0 && attempt <= MAX_RECONNECT_ATTEMPTS) {
            this.disconnectStatusText.setText(I18n.t('disconnect.reconnecting', { attempt: String(attempt), max: String(MAX_RECONNECT_ATTEMPTS) }));
          }
        },
      });
    }
  }

  private hideDisconnectOverlay(): void {
    if (!this.disconnectOverlay) return;

    // Fade out and destroy
    this.tweens.add({
      targets: this.disconnectOverlay,
      alpha: 0,
      duration: 300,
      onComplete: () => {
        this.disconnectOverlay?.destroy();
        this.disconnectOverlay = null;
        this.disconnectStatusText = null;
        this.disconnectButton = null;
      },
    });

    // Re-enable input
    this.chatInputFocused = false;
  }

  private onNetworkError(data: { error?: string; message?: string; code?: string }): void {
    const msg = data.message || data.error || 'An error occurred';
    // Show toast for dock failures and other actionable errors
    if (data.code === 'DOCK_FAILED' || data.code === 'INVALID_DOCK' || msg.includes('docking')) {
      ToastNotification.show(this, msg, { color: 0xff4444 });
    }
  }

  private onHexClicked(data: { hex: any; entities: any[] }): void {
    if (data.entities.length > 0) {
      const ownedUnit = data.entities.find((e) => 'attackRange' in e && e.ownerId === this.mmoState.getUserId());
      const entity = ownedUnit || data.entities[0];
      let info = '';
      if ('attackRange' in entity) {
        info = `Unit: ${entity.type}\nHP: ${entity.currentHp}/${entity.maxHp}`;
      } else if ('buildings' in entity) {
        info = `Planet: ${entity.name}\nHP: ${entity.currentHp}/${entity.maxHp}`;
      } else {
        info = `Asteroid\nRichness: ${entity.resourceRichness}`;
      }
      console.log('[MMOHUD] Entity info:', info);
    }
  }

  private onOpenAllianceDialog(): void {
    new EnhancedAllianceDialog(this, this.mmoState, this.mmoNetwork);
  }

  private onOpenDMDialog(): void {
    new DirectMessageDialog(this, this.mmoState);
  }

  private onBrowseAlliances(): void {
    new BrowseAlliancesDialog(this, this.mmoState, this.mmoNetwork);
  }

  private onOpenLeaderboard(): void {
    new AllianceLeaderboardDialog(this, this.mmoNetwork);
  }

  private onAllianceUpdated(): void {
    // rightPanel removed
  }

  private onOpenShipProgression(data: { unitId: string }): void {
    this.shipProgressionPanel.show(data.unitId);
  }

  private onOpenComponentUpgrade(data: { unitId: string }): void {
    this.componentUpgradeDialog.show(data.unitId);
  }

  private onOpenComponentStore(data?: { category?: string; componentId?: string; componentCategory?: string; shipId?: string }): void {
    const category = (data?.componentCategory || data?.category) as import('../../shared/common/MonetizationTypes').ComponentCategory | undefined;
    new ComponentMarketplaceDialog(this, category, data?.componentId, data?.shipId);
  }

  private onOpenMissionPanel(): void {
    this.missionPanel.show();
  }

  private onOpenStationManagement(): void {
    new StationManagementDialog(this);
  }

  private onOpenProfileDialog(): void {
    new ProfileDialog(this);
  }

  private onOpenEnemyProfile(data: { userId: string }): void {
    new EnemyProfileDialog(this, data.userId);
  }

  private onOpenEnemyShip(data: { userId: string; shipId: string }): void {
    new EnemyShipDialog(this, data.userId, data.shipId);
  }

  private onOpenWreckageDialog(): void {
    new WreckageDialog(this);
  }

  private onOpenCrewManagement(): void {
    new CrewManagementDialog(this);
  }

  private onOpenInsuranceDialog(): void {
    new InsuranceDialog(this);
  }

  private onOpenShipModification(data: { unitId: string; stationId: string }): void {
    if (data?.unitId && data?.stationId) {
      new ShipModificationDialog(this, data.unitId, data.stationId);
    }
  }

  private onOpenStationDesign(data: { stationId: string }): void {
    if (data?.stationId) {
      new ShipDesignDialog(this, data.stationId);
    }
  }

  private onOpenStationResearch(data: { stationId: string }): void {
    if (data?.stationId) {
      new ResearchBoardDialog(this, data.stationId);
    }
  }

  private onOpenStationTech(data: { stationId: string }): void {
    if (data?.stationId) {
      new TechListDialog(this, data.stationId);
    }
  }

  private onShowOfflineRecap(events: OfflineRecapEvent[]): void {
    if (events && events.length > 0) {
      new OfflineRecapDialog(this, events);
    }
  }

  // onToggleResourceDisplay removed

  private onWorldJoined(_payload: any): void {
    console.log('[MMOHUDScene] onWorldJoined fired — requesting resources');
    EventBus.emit('send_message', { type: 'mmo_get_resources', payload: { worldId: 1 } });
  }

  private onPlayerResources(payload: any): void {
    console.log('[MMOHUDScene] onPlayerResources received:', payload);
    if (payload && typeof payload.credits === 'number') {
      console.log('[MMOHUDScene] Setting resources to:', payload.credits);
      this.mmoState.setResources(payload.credits);
      this.resourceText?.setText(payload.credits.toString());
    }
  }

  /**
   * Dispatch table for the mobile nav drawer. Each NavKey maps to either a
   * dialog instantiation or an EventBus emit — mirroring the desktop top bar
   * `NAV_BUTTONS.action` entries so mobile users get full feature parity.
   */
  public handleMobileNav(key: string): void {
    switch (key) {
      // You
      case 'profile':          new ProfileDialog(this); break;
      case 'how_to':           new HowToGuideDialog(this, false); break;
      // Commerce
      case 'shop':             new ShipStoreDialog(this); break;
      case 'buy_isd':          new BuyISDDialog(this); break;
      case 'trade':            new AuctionHouseDialog(this); break;
      case 'parts':            new ComponentMarketplaceDialog(this); break;
      // Operations
      case 'ore':              new MineralInventoryDialog(this); break;
      case 'crew':             new CrewManagementDialog(this); break;
      case 'station':          new StationManagementDialog(this); break;
      case 'wreck':            new WreckageDialog(this); break;
      case 'insure':           new InsuranceDialog(this); break;
      // Research
      case 'research':         new RelicInventoryDialog(this); break;
      case 'tech':             new TechListDialog(this); break;
      // Alliance
      case 'alliance_hq':      new AllianceHQDialog(this); break;
      case 'alliance':         EventBus.emit('mmo-open-alliance-dialog'); break;
      case 'browse_alliances': EventBus.emit('mmo-browse-alliances'); break;
      case 'leaderboard':      EventBus.emit('mmo-open-leaderboard'); break;
      case 'dm':               EventBus.emit('mmo-open-dm-dialog'); break;
      // Camera
      case 'center':           EventBus.emit('center-on-player-ship'); break;
      case 'follow':           EventBus.emit('toggle-camera-follow'); break;
      // Wallet
      case 'refund':           new RefundDialog(this); break;
      case 'withdraw':         new WithdrawDialog(this); break;
      default:
        console.warn('[MMOHUDScene] Unknown mobile nav key:', key);
    }
  }

  update(time: number, delta: number): void {
    if (time % 1000 < delta) {
      const tick = this.mmoState.getTick();
      this.tickText?.setText(`Turn ${tick}`);
      const resources = this.mmoState.getResources();
      this.resourceText?.setText(resources.toString());

      // Refresh minimap every tick (1s) so it tracks player position & nearby entities
      this.territoryMap?.refresh();
    }
  }

  shutdown(): void {
    console.log('[MMOHUD] Shutting down...');
    this.removeEventListeners();
    // Clean up keyboard listeners
    this.shortcutKeys.forEach((k) => k.removeAllListeners());
    this.shortcutKeys = [];
    // Clean up chat focus listeners
    EventBus.off('chat-input-focused', this.onChatFocused, this);
    EventBus.off('chat-input-unfocused', this.onChatUnfocused, this);
  }
}
